import { useState } from 'react';
import { Button, Chip } from '@ds';
import { MdOutlineClose, MdKeyboardArrowDown, MdKeyboardArrowUp, MdOutlineClear } from 'react-icons/md';
import { BulkAssignmentCase } from '@/api/apis/case';
import { getColorsByOrganType, ORGAN_ICON } from '@/utils/helpers';

type BulkAssignmentSelectedCasesProps = {
  selectedItems: BulkAssignmentCase[];
  onRemove: (id: string) => void;
  onClear: () => void;
};

const ITEMS_TO_SHOW_COLLAPSED = 20;

export const BulkAssignmentSelectedCases = ({ selectedItems, onRemove, onClear }: BulkAssignmentSelectedCasesProps) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const shouldShowToggle = selectedItems.length > ITEMS_TO_SHOW_COLLAPSED;
  const displayAll = isExpanded || !shouldShowToggle;
  const itemsToDisplay = displayAll ? selectedItems : selectedItems.slice(0, ITEMS_TO_SHOW_COLLAPSED);
  const hiddenCount = selectedItems.length - ITEMS_TO_SHOW_COLLAPSED;

  return (
    <div className="bg-white rounded-xl p-3 flex flex-row items-center gap-2 min-h-[52px]">
      <div className="flex gap-1 items-center">
        <span className="text-m-sb text-base-5">Selected cases</span>
        {selectedItems.length ? (
          <Chip size="sm" className="px-1 bg-base-60">
            {selectedItems.length}
          </Chip>
        ) : (
          <span className="text-s-m text-base-10">Nothing selected yet</span>
        )}
      </div>
      <div className="flex flex-1 gap-1 flex-wrap items-center">
        {itemsToDisplay.map((item) => {
          const OrganIcon = ORGAN_ICON[item.organType];
          const colors = getColorsByOrganType(item.organType);
          return (
            <Chip
              key={item.caseId}
              onClose={() => onRemove(item.caseId)}
              size="md"
              classNames={{ base: 'px-2', content: 'px-0 pr-2 flex items-center gap-1' }}
              className="bg-base-60"
              endContent={<MdOutlineClose />}
            >
              <OrganIcon className={colors.textSaturated} size={16} />
              {item.caseName}
            </Chip>
          );
        })}
        {shouldShowToggle && (
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="h-6 px-2 py-1 rounded-[19.2px] flex items-center gap-1 hover:bg-base-80 transition-colors"
          >
            <span className="text-xs-sb text-base-5 leading-none">
              {isExpanded ? 'Show less' : `Show ${hiddenCount} more`}
            </span>
            {isExpanded ? <MdKeyboardArrowUp size={14} /> : <MdKeyboardArrowDown size={14} />}
          </button>
        )}
        {selectedItems.length > 0 && (
          <Button endContent={<MdOutlineClear size={18} />} size="sm" variant="light" onPress={onClear}>
            Clear all
          </Button>
        )}
      </div>
    </div>
  );
};
